import { useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@buildpy/ui/alert-dialog";
import { Checkbox } from "@buildpy/ui/checkbox";
import { setContractPaymentPaid } from "@/api/actions/change-order";
import { useRecordAction } from "@/modules/project/ui/use-record-action";

interface Props {
  contractId: string;
  paymentId: string;
  label: string;
  paid: boolean;
  disabled: boolean;
}

// 지급회차 받음 표시 — 보낸 변경공사가 있으면 서버가 needs_confirm 으로 막고, 확인하면 force 로 다시 보낸다.
const PaymentPaidToggle = ({ contractId, paymentId, label, paid, disabled }: Props) => {
  const { pending, run } = useRecordAction();
  const [ask, setAsk] = useState<{ next: boolean; message: string } | null>(null);

  const record = (next: boolean, force: boolean) => {
    let asked = false;
    void run(
      "지급 기록을 남기지 못했습니다",
      async () => {
        const result = await setContractPaymentPaid(contractId, paymentId, next, force);
        if (!result.ok && !force && result.needs_confirm) {
          asked = true;
          setAsk({ next, message: result.message });
          return { ok: true };
        }
        return result;
      },
      () => {
        if (!asked) toast.success(next ? `${label} 받음으로 기록했습니다` : `${label} 받음을 취소했습니다`);
      }
    );
  };

  return (
    <>
      <Checkbox
        aria-label={`${label} 받음`}
        checked={paid}
        disabled={disabled || pending}
        onCheckedChange={(next) => record(next === true, false)}
      />
      <AlertDialog open={ask !== null} onOpenChange={(open) => !open && setAsk(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{label} 지급 기록을 바꿀까요?</AlertDialogTitle>
            <AlertDialogDescription>{ask?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (ask) record(ask.next, true);
                setAsk(null);
              }}
            >
              그래도 기록
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PaymentPaidToggle;
